import AuthCli from './AuthCli'

/**
 * JavaScript version of the Outbox service API.
 */
export default class UserCli {

  //
  // Path constants:
  //
  static get SERVICE_ROUTE() { return 'user' }
  static get ADDRESSES_PATH() { return 'addresses' }
  static get ADDRESS_ID_PATH() { return 'addressid' }
  static get EMAILS_PATH() { return 'emails' }
  static get EMAIL_PATH() { return 'email' }
  static get USER_ID_QUERY() { return 'userid' }
  static get BILLABLE_ID_QUERY() { return 'billableid' }
  static get TOKEN_QUERY() { return 'token' }
  static get CULTURE_PATH() { return 'culture' }
  static get EMISSION_EMAIL_PATH() { return 'emissionemail' }
  static get CERTIFICATE_EMAIL_PATH() { return 'certificatemail' }
  static get DEFAULT_SENDER_PATH() { return 'defaultsender' }

  // URL prefix for debugging environment vs behind reverse proxy environment.
  static get devPrefix() { return (!process.env.NODE_ENV || process.env.NODE_ENV === 'development') ? 'http://localhost:6016/' : window.location.protocol + '//' + window.location.host + '/api/' }

  //
  // Postal addresses:
  //
  
  /**
   * Provides the list of stored postal addresses of the calling user.
   * @returns 
   */
  static async GetAddresses() {
    var verb = 'GET'
    var path = this.SERVICE_ROUTE + '/' + this.ADDRESSES_PATH
    var body = null

    return AuthCli.Fetch(this.devPrefix + path, verb, body)
  }
        
  /**
   * Saves a new or existing postal address.
   * @param {PostalAddress} address Saves a new or existing postal address.
   * @returns Saves a new or existing postal address.
   */
  static async StoreAddress(address) {
    var verb = 'PUT'
    var path = this.SERVICE_ROUTE + '/' + this.ADDRESSES_PATH
    var body = address

    return AuthCli.Fetch(this.devPrefix + path, verb, body)
  }

  /**
   * Removes from the list of stored postal addresses the one specified by its unique identifier.
   * @returns 
   */
  static async RemoveAddress(addressId) {
    var verb = 'DELETE'
    var path = this.SERVICE_ROUTE + '/' + this.ADDRESSES_PATH + '/' + addressId
    var body = null

    return AuthCli.Fetch(this.devPrefix + path, verb, body)
  }

  //
  // E-mail addresses:
  //

  /**
   *  Provides the list of stored email addresses of the calling user.
   *  Not yet validated email addresses are included but including a leading asterisk (*). Eg: "*name@company.com"
   * @returns 
   */
  static async GetEmails() {
    var verb = 'GET'
    var path = this.SERVICE_ROUTE + '/' + this.EMAILS_PATH
    var body = null

    return AuthCli.Fetch(this.devPrefix + path, verb, body)
  }
       
  /**
   * Saves a new or existing email address.
   * @param {string} email The email address to store
   * @returns 
   */
  static async StoreEmail(email) {
    var verb = 'POST'
    var path = this.SERVICE_ROUTE + '/' + this.EMAILS_PATH
    var body = email

    return AuthCli.Fetch(this.devPrefix + path, verb, body)
  }
        
  /**
   * Validates a previously stored email address by demonstrating the reception
   * of a validation token. This token was passed via an e-mail message.
   * @param {string} email Email address to be validated
   * @param {string} userId Internal identifier of the user attempting to validate an e-mail
   * @param {string} billableId Internal identifier of the billable this e-mail address is bound to
   * @param {string} token Validation token as received via e-mail message
   * @returns 
   */
  static async ValidateEmail(email, userId, billableId, token) {
    var verb = 'PUT'
    var path = this.SERVICE_ROUTE + '/' + this.EMAILS_PATH + "?" +
      this.USER_ID_QUERY + "=" + userId + "&" +
      this.BILLABLE_ID_QUERY + "=" + billableId + "&" +
      this.TOKEN_QUERY + "=" + token 
    var body = email

    return AuthCli.Fetch(this.devPrefix + path, verb, body)
  }
        
  /**
   * Removes an email address from the list of stored email addresses.
   * @returns 
   */
  static async RemoveEmail(email) {
    var verb = 'DELETE'
    var path = this.SERVICE_ROUTE + '/' + this.EMAILS_PATH + '/' + email
    var body = null

    return AuthCli.Fetch(this.devPrefix + path, verb, body)
  }

  //
  // Settings:
  //

  /**
   * Provides the current culture of the calling user.
   * @returns ISO code of the language: es, en, pt,...
   */
  static async GetCulture() {
    var verb = 'GET'
    var path = this.SERVICE_ROUTE + '/' + this.CULTURE_PATH
    var body = null

    return AuthCli.Fetch(this.devPrefix + path, verb, body)
  }
        
  /**
   * Establishes the culture for the calling user.
   * @param {string} culture ISO code of the language: es, en, pt,...
   * @returns 
   */
  static async SetCulture(culture) {
    var verb = 'PUT'
    var path = this.SERVICE_ROUTE + '/' + this.CULTURE_PATH
    var body = culture

    return AuthCli.Fetch(this.devPrefix + path, verb, body)
  }

  /**
   * Provides the current e-mail address set to be the receiver of e-mail messages
   * for shipment emission confirmations.
   * @returns 
   */
  static async GetShipmentEmissionEmail() {
    var verb = 'GET'
    var path = this.SERVICE_ROUTE + '/' + this.EMISSION_EMAIL_PATH
    var body = null

    return AuthCli.Fetch(this.devPrefix + path, verb, body)
  }
        
  /**
   * Establishes the e-mail address to be the receiver of e-mail messages
   * for shipment emission confirmations from now on.
   * @param {string} email E-mail address. Must be a registered (with StoreEmail method) and validated (with ValidateEmail method) one
   * @returns 
   */
  static async SetShipmentEmissionEmail(email) {
    var verb = 'PUT'
    var path = this.SERVICE_ROUTE + '/' + this.EMISSION_EMAIL_PATH
    var body = email

    return AuthCli.Fetch(this.devPrefix + path, verb, body)
  }

  /**
   * Provides the current e-mail address set to be the receiver of e-mail messages
   * containing PEE and centificate documents.
   * @returns 
   */
  static async GetCertificateEmail() {
    var verb = 'GET'
    var path = this.SERVICE_ROUTE + '/' + this.CERTIFICATE_EMAIL_PATH
    var body = null

    return AuthCli.Fetch(this.devPrefix + path, verb, body)
  }
        
  /**
   * Establishes the e-mail address to be the receiver of e-mail messages
   * containing PEE and certificate documents.
   * @param {string} email E-mail address. Must be a registered (with StoreEmail method) and validated (with ValidateEmail method) one
   * @returns 
   */
  static async SetCertificateEmail(email) {
    var verb = 'PUT'
    var path = this.SERVICE_ROUTE + '/' + this.CERTIFICATE_EMAIL_PATH
    var body = email

    return AuthCli.Fetch(this.devPrefix + path, verb, body)
  }

  /**
   * Provides the current default sender address.
   * This is the address to automatically propose for new shipments.
   * @returns 
   */
  static async GetSenderAddress() {
    var verb = 'GET'
    var path = this.SERVICE_ROUTE + '/' + this.DEFAULT_SENDER_PATH
    var body = null

    return AuthCli.Fetch(this.devPrefix + path, verb, body)
  }
        
  /**
   * Establishes the postal address, of those registered, to be the default to be automatically proposed in new shipments.
   * @param {string} addressId Internal identifier of the postal address. Pass null to delete previous set
   * @returns 
   */
  static async SetDefaultSender(addressId) {
    var verb = 'PUT'
    var path = this.SERVICE_ROUTE + '/' + this.DEFAULT_SENDER_PATH
    var body = addressId

    return AuthCli.Fetch(this.devPrefix + path, verb, body)
  }
}


// WEBPACK FOOTER //
// ./src/sdk/UserCli.js